import { Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { MqttConnectionState } from "ngx-mqtt";
import { Subscriber, Subscription } from "rxjs";
import { Environment } from "src/components/shared-components/class/general-classes/environment";
import { AccessResult } from "src/components/shared-components/enum/enumAccessControl";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { AlertService } from "src/service/alert.service";
import { EnvironmentService } from "src/service/environment.service";
import { LoaderService } from "src/service/loader.service";
import { MqttClientService } from "src/service/mqtt-client.service";
import { NetworkService } from "src/service/network.service";
import { TranslationService } from "src/service/translation.service";

@Component({
    selector: "app-list-environment-open-door",
    templateUrl: "./list-environment-open-door.component.html",
    styleUrls: ["./list-environment-open-door.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ListEnvironmentOpenDoorComponent implements OnInit {
  private _translateSubscription: Subscription = null;
  private _networkServiceSubscription: Subscription = null;

  //#region Translated strings
  public lblCurrentSearchPlaceholder: string = "";
  //#endregion

  public searchText: string = "";
  public environmentsFiltered: Array<Environment> = null;

  public _lblCheckingAccess: string = "Controllo esito in corso";
  public isOnline: boolean = false;

  @Input() environments: Array<Environment>;

  constructor(
    private _environmentService: EnvironmentService,
    private _translateService: TranslateService,
    private _alertService: AlertService,
    private _loaderService: LoaderService,
    private _mqttClientService: MqttClientService,
    private _translationService: TranslationService,
    private _networkService: NetworkService
  ) { }

  ngOnInit() {
    this.subscribeNetworkServiceData();
    this.loadTranslation();
    this.subscribeTranslationEvent();
  }

  ngOnDestroy() {
    if (this._networkServiceSubscription) {
      this._networkServiceSubscription.unsubscribe();
    }
  }

  /**
   * Handle release action on environment icon
   *
   * @param idEnvironment id environment icon release
   */
  public onReleaseEnvironmentButton(idEnvironment: number) {
    document
      .getElementById("environment-icon-" + idEnvironment)
      .classList.remove("bigstudio3-door-open");
    document
      .getElementById("environment-icon-" + idEnvironment)
      .classList.add("bigstudio3-door-close");
  }

  /**
   * Handle pressed action on environment icon: send the message to request the open door and show the feedback
   *
   * @param idEnvironment id environment icon pressed
   */
  public async onPressEnvironmentButton(idEnvironment: number) {
    if (this._mqttClientService.Status === MqttConnectionState.CONNECTED) {
      await this.handlePressOpenDoorCommand(idEnvironment);
    } else {
      await this._alertService.presentAlertCommandNotAvaiable();
    }
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.searchEnvironmentAccess",
        "dictionary.checkingAccessResult",
      ])
      .subscribe((translation) => {
        this.lblCurrentSearchPlaceholder =
          translation["dictionary.searchEnvironmentAccess"];
        this._lblCheckingAccess =
          translation["dictionary.checkingAccessResult"];
      });
  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * Handle subscribtion to network service
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.isOnline = !(currentConnectionType === ConnectionType.NONE);
        }
      );
  }

  /**
   * Handle normal press open door command button action
   *
   * @param idEnvironment idEnvironment button action
   */
  private async handlePressOpenDoorCommand(
    idEnvironment: number
  ): Promise<void> {
    try {
      document
        .getElementById("environment-icon-" + idEnvironment)
        .classList.add("bigstudio3-door-open");
      document
        .getElementById("environment-icon-" + idEnvironment)
        .classList.remove("bigstudio3-door-close");

      await this._loaderService.presentLoaderWaiting();

      const accessResult: AccessResult =
        await this._environmentService.openDoorEnvironmentRequestCommand(
          idEnvironment
        );

      await this._loaderService.dismissLoaderWaiting();
      await this._alertService.presentAlertAccessResult(accessResult);
      document
        .getElementById("environment-icon-" + idEnvironment)
        .classList.remove("bigstudio3-door-open");
      document
        .getElementById("environment-icon-" + idEnvironment)
        .classList.add("bigstudio3-door-close");
    } catch (exception) {
    } finally {
      await this._loaderService.dismissLoaderWaiting();
    }
  }
}
